import { storeToRefs } from "pinia";

import { useArticlesStore } from "@/stores/Articles";
import { useBonusReportsStore } from "@/stores/BonusReports";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";
import { useWebinarsStore } from "@/stores/Webinars";

import { useContentReadState } from "@/composables/content-read-state";

import {
    articleKey,
    bonusReportKey,
    categoryContextKey,
    fiveStarTradeAlertKey,
    scannerKey,
    tradeAlertKey,
    webinarKey,
} from "@/types/products/product-definitions";

import { productFactories } from "@/plugins/ProductContextsPlugin";

import type { ActionConfig } from "@/types/products/product-config";

/*****************************************************************************
 * Single source of truth for the per-tab unread count shown in a product
 * workspace AND for the per-product aggregate shown in the sidebar.
 *
 * Counted per (content-type × category) pair rather than per distinct item,
 * so a single article tagged with multiple known sub-categories (e.g.
 * shadow-spike-alert AND spotlight-videos) contributes once to Scanner
 * History, once to Spotlight Videos, and twice to the sidebar product
 * badge. This matches the user mental model of "sum the visible tab
 * badges" — workspace tabs and sidebar badge always reconcile.
 *****************************************************************************/
export function useProductActionUnreadCounts() {
    const { allArticles } = storeToRefs( useArticlesStore() );
    const { allBonusReports } = storeToRefs( useBonusReportsStore() );
    const { allTradeAlerts } = storeToRefs( useTradeAlertsStore() );
    const { allWebinars } = storeToRefs( useWebinarsStore() );
    const { unreadCountForContent } = useContentReadState();

    function actionUnreadCount( action: ActionConfig | undefined, productSlug: string ): number {
        if ( !action ) return 0;

        const componentKey = action.componentKey;
        const categorySlug = action.ctx?.get( categoryContextKey ) as string | undefined;
        const options = { productSlug, categorySlug };

        if ( componentKey === articleKey || componentKey === scannerKey ) {
            return unreadCountForContent( allArticles.value, articleKey, options );
        }

        if ( componentKey === bonusReportKey ) {
            return unreadCountForContent( allBonusReports.value, bonusReportKey, options );
        }

        if ( componentKey === tradeAlertKey || componentKey === fiveStarTradeAlertKey ) {
            return unreadCountForContent( allTradeAlerts.value, tradeAlertKey, options );
        }

        if ( componentKey === webinarKey ) {
            return unreadCountForContent( allWebinars.value, webinarKey, options );
        }

        return 0;
    }

    // Sums every action's count in the given product's workspace — i.e.
    // matches the visible tab-badge total. A multi-tagged article counts
    // once per matching category tab.
    function productTabsTotalUnreadCount( productSlug: string ): number {
        const factory = productFactories[ productSlug ];
        if ( !factory ) return 0;

        const actionsMap = factory().actionsMap;
        let total = 0;
        for ( const action of actionsMap.values() ) {
            total += actionUnreadCount( action, productSlug );
        }
        return total;
    }

    return {
        actionUnreadCount,
        productTabsTotalUnreadCount,
    };
}
